import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { track, sessionId } from "@/lib/track";
import { toast } from "sonner";
import { Search, ArrowRight, CheckCircle2, MailCheck, UserPlus } from "lucide-react";

export default function FindProfile() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [claimState, setClaimState] = useState(null); // {id, sent, email_hint} | {id, reason}
  const nav = useNavigate();

  const search = async (e) => {
    e?.preventDefault();
    if (q.trim().length < 3) { toast.error("Type at least 3 characters"); return; }
    setBusy(true);
    setClaimState(null);
    track("profile_search", { q });
    try {
      const { data } = await api.get(`/public/coach-search?q=${encodeURIComponent(q.trim())}&session_id=${sessionId()}`);
      setResults(data.results);
    } catch {
      toast.error("Search failed — try again");
    } finally { setBusy(false); }
  };

  const claim = async (r) => {
    setBusy(true);
    try {
      const { data } = await api.post("/public/claim-request", { coach_profile_id: r.id, session_id: sessionId() });
      setClaimState({ id: r.id, ...data });
      if (!data.sent && data.reason === "cooldown") toast.info(`We already sent your claim link to ${data.email_hint} — check your inbox and spam. You can request another in ${data.retry_after}s.`);
      else if (!data.sent) toast.info("No email on file for this profile — create your account instead.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start claim");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-appbg text-white px-4 md:px-8 py-12" data-testid="find-profile-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-xs tracking-widest text-blaze">STEP 1 OF 2 · FREE</div>
        <h1 className="heading text-4xl sm:text-5xl mt-2">LET&apos;S SEE IF WE ALREADY HAVE YOUR PROFILE.</h1>
        <p className="text-neutral-400 mt-3 text-sm md:text-base">
          We mapped the lacrosse coaching world from public records — search your name, school or email.
        </p>

        <form onSubmit={search} className="mt-8 flex gap-2">
          <input
            data-testid="profile-search-input" autoFocus
            className="flex-1 bg-appcard border border-neutral-700 px-4 py-3.5 text-base outline-none focus:border-blaze min-h-[52px]"
            placeholder="Your name, school/program, or email"
            value={q} onChange={(e) => setQ(e.target.value)}
          />
          <button type="submit" disabled={busy} data-testid="profile-search-btn"
            className="bg-blaze text-white px-5 heading text-base hover:glow-blaze transition-all disabled:opacity-50 flex items-center gap-2 min-h-[52px]">
            <Search size={16} /> {busy ? "…" : "SEARCH"}
          </button>
        </form>

        {results !== null && (
          <div className="mt-8" data-testid="profile-search-results">
            {results.length === 0 ? (
              <div className="border border-dashed border-neutral-700 p-8 text-center" data-testid="profile-not-found">
                <div className="heading text-2xl">NO PROFILE YET — LET&apos;S BUILD YOURS.</div>
                <p className="text-neutral-400 text-sm mt-2">Two minutes, free forever, fully private.</p>
                <button data-testid="create-profile-btn" onClick={() => { track("profile_created", { via: "search_miss" }); nav("/auth?role=coach"); }}
                  className="mt-5 inline-flex items-center gap-2 bg-blaze text-white heading text-lg px-7 py-3.5 hover:glow-blaze transition-all">
                  <UserPlus size={16} /> CREATE MY PROFILE
                </button>
              </div>
            ) : (
              <>
                <div className="text-xs tracking-widest text-neutral-500 mb-3">{results.length} PROFILE{results.length > 1 ? "S" : ""} FOUND — IS THIS YOU?</div>
                <div className="space-y-3">
                  {results.map((r) => (
                    <div key={r.id} className="border border-neutral-800 bg-appcard p-5" data-testid={`profile-result-${r.id}`}>
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                          <div className="heading text-xl">{r.name}</div>
                          <div className="text-sm text-neutral-400">{[r.role, r.organization].filter(Boolean).join(" · ")}</div>
                        </div>
                        {r.claimed ? (
                          <Link to="/auth" data-testid={`result-claimed-${r.id}`} className="text-xs text-neutral-500 border border-neutral-700 px-3 py-2">ALREADY CLAIMED — LOG IN</Link>
                        ) : claimState?.id === r.id ? (
                          claimState.sent || claimState.reason === "cooldown" ? (
                            <div className="flex items-center gap-2 text-volt text-sm" data-testid={`claim-sent-${r.id}`}>
                              <MailCheck size={16} /> {claimState.sent ? "Claim link sent to" : "Link already sent to"} {claimState.email_hint}
                            </div>
                          ) : (
                            <button data-testid={`claim-noemail-${r.id}`} onClick={() => nav("/auth?role=coach")}
                              className="bg-blaze text-white heading text-sm px-4 py-2.5 hover:glow-blaze">CREATE ACCOUNT TO CLAIM</button>
                          )
                        ) : (
                          <button data-testid={`claim-btn-${r.id}`} onClick={() => claim(r)} disabled={busy}
                            className="inline-flex items-center gap-2 bg-blaze text-white heading text-sm px-4 py-2.5 hover:glow-blaze transition-all disabled:opacity-50">
                            <CheckCircle2 size={14} /> THIS IS ME — CLAIM IT
                          </button>
                        )}
                      </div>
                      {claimState?.id === r.id && (claimState.sent || claimState.reason === "cooldown") && (
                        <p className="text-xs text-neutral-500 mt-3" data-testid={`claim-sent-note-${r.id}`}>
                          {claimState.sent
                            ? "To keep your profile secure we sent the claim link to the email on file. Check your inbox (and spam) — it takes one click."
                            : `A claim link was sent moments ago — check your inbox and spam. You can request another in about ${claimState.retry_after}s.`}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-6 text-sm text-neutral-400">
                  None of these you?{" "}
                  <button data-testid="create-profile-fallback" onClick={() => { track("profile_created", { via: "not_me" }); nav("/auth?role=coach"); }} className="text-blaze underline underline-offset-4">
                    Create your CoachConnect profile
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        <Link to="/" data-testid="find-back-home" className="mt-12 inline-flex items-center gap-2 text-xs text-neutral-500 hover:text-neutral-300">
          <ArrowRight size={12} className="rotate-180" /> Back to home
        </Link>
      </div>
    </div>
  );
}
